import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EditFieldRenderer  from './EditFieldRenderer';
import { getFormattedArrayFields } from './ArrayFieldSection';
import './DynamicEditForm.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WaveLoader from '../WaveLoader/WaveLoader';


const DynamicEditForm = ({ fields, apiUrl, successRedirect }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [dropdownData, setDropdownData] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false); // <-- NEW


  const { id } = useParams();
  const navigate = useNavigate();


  // Fetch current entry
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${apiUrl}/${id}`);
        const data = res.data;

        const updated = { ...data };
        fields
          .filter((f) => f.api && f.type === 'ObjectId')
          .forEach((f) => {
            if (data[f.fields] && typeof data[f.fields] === 'object') {
              updated[f.fields] = data[f.fields]._id;
              updated[`${f.fields}_display`] = data[f.fields][f.apiField] || '';
            }
          });

        setFormData(updated);
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [id, apiUrl, fields]);

  // Fetch dropdowns
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        for (const field of fields) {
          if (field.api) {
            const res = await axios.get(field.api);
            const options = res.data.map((item) => ({
              id: item._id || item.id,
              label: item[field.apiField] || item.name,
            }));
  
            if (
              formData[field.fields] &&
              !options.find((opt) => opt.id === formData[field.fields])
            ) {
              options.unshift({
                id: formData[field.fields],
                label: `Previously selected (${formData[field.fields]})`,
              });
            }
  
            setDropdownData((prev) => ({
              ...prev,
              [field.fields]: options,
            }));
          }
        }
      } catch (err) {
        console.error("Dropdown fetch failed", err);
        toast.error("⚠️ Failed to load dropdown options.");
      } finally {
        setDropdownsLoaded(true); // ✅ Mark dropdowns done
      }
    };
  
    if (!fetching) fetchDropdowns();
  }, [fields, fetching, formData]);
  

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      [`${name}_display`]: dropdownData[name]?.find((i) => i.id === value)?.label || "",
    });
  };

  const handleArrayChange = (fieldName, index, subField, value) => {
    if (index === null && subField === null) {
      // ✅ This handles checkBox type fields
      setFormData({ ...formData, [fieldName]: value });
      return;
    }
  
    const updated = [...(formData[fieldName] || [])];
    if (!updated[index]) updated[index] = {};
    updated[index][subField] = value;
    setFormData({ ...formData, [fieldName]: updated });
  };
  
  

  const handleAddItem = (fieldName) => {
    setFormData({ ...formData, [fieldName]: [...(formData[fieldName] || []), {}] });
  };

  const handleRemoveItem = (fieldName, index) => {
    const updated = [...(formData[fieldName] || [])];
    updated.splice(index, 1);
    setFormData({ ...formData, [fieldName]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    const processedData = getFormattedArrayFields(formData, fields);
    console.log("Submitting:", processedData);
  
    try {
      await axios.put(`${apiUrl}/${id}`, processedData);
      navigate(successRedirect);
    } catch (err) {
      console.error("Submission failed:", err);
      toast.error("❌ Failed to update. Please check the fields and try again.");
    } finally {
      setLoading(false);
    }
  };
  
  

  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [formData]);

  if (fetching || !dropdownsLoaded) return <WaveLoader />;

  return (
    <div className="dynamic-form">
      <h2 className="form-title">Edit Entry</h2>
      <ToastContainer position="top-right" autoClose={4000} />

      <form onSubmit={handleSubmit} className="form-wrapper">
        {fields.map((field, idx) => (
          <EditFieldRenderer
            key={idx}
            field={field}
            formData={formData}
            setFormData={setFormData}
            dropdownData={dropdownData}
            handleInputChange={handleInputChange}
            handleDropdownChange={handleDropdownChange}
            handleArrayChange={handleArrayChange}
            handleAddItem={handleAddItem}
            handleRemoveItem={handleRemoveItem}
          />
        ))}
        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? 'Updating...' : 'Update Entry'}
        </button>
        {successMessage && <p className="success-message">{successMessage}</p>}
      </form>
    </div>
  );
};

export default DynamicEditForm;
